/**
 * `receipts.*` — customer payment ledger + per-invoice allocation.
 *
 * V1 substitute for embedded payment links (system-decisions §13). Operator
 * records the payment after it lands in the bank, allocates across one or
 * more invoices, and the invoice statuses flip accordingly. TDS is captured
 * per receipt with a snapshot of the customer's TDS section so later
 * customer-master changes don't rewrite this row's tax context.
 */
import { z } from "zod";
import { uuid, paged, isoDate, moneyPaise, signedPaise } from "../_common.js";

/** Mode the customer used to pay. Mirrors the schema enum. */
export const receiptMode = z.enum([
  "upi",
  "neft",
  "rtgs",
  "imps",
  "cheque",
  "cash",
  "card",
  "other",
]);

export const receiptSettlementKind = z.enum([
  "foreign_bank_charge",
  "indian_bank_charge",
  "bank_charge_gst",
  "round_off",
  "write_off",
  "other_deduction",
  "other_addition",
]);

export const receiptSettlementDirection = z.enum(["deduction", "addition"]);

export const receiptSettlementLineInput = z.object({
  /**
   * Client-side stable id (e.g. a uuid generated in the dialog). Used
   * exclusively to express parent→child relationships within the same
   * recordReceipt call (a `bank_charge_gst` line references its
   * `indian_bank_charge` parent by this id). The server discards it on
   * insert — DB rows get fresh uuids. ADR-0019.
   */
  clientId: z.string().max(80).optional(),
  /** Client-side reference to a sibling line's `clientId`. ADR-0019. */
  parentClientId: z.string().max(80).optional(),
  invoiceId: uuid.optional(),
  kind: receiptSettlementKind,
  direction: receiptSettlementDirection,
  label: z.string().trim().min(1).max(80),
  amountPaise: moneyPaise,
  currencyCode: z
    .string()
    .trim()
    .regex(/^[A-Z]{3}$/)
    .default("INR"),
  foreignAmountMinor: moneyPaise.optional(),
  fxRateToInrPaise: moneyPaise.optional(),
  note: z.string().trim().max(160).optional(),
});

export const receiptSettlementLine = z.object({
  id: uuid,
  receiptId: uuid,
  invoiceId: uuid.nullable(),
  /** Self-reference to a parent settlement-line id. GST sub-lines reference
   *  their `indian_bank_charge` parent. Null for top-level lines. ADR-0019. */
  parentLineId: uuid.nullable(),
  kind: receiptSettlementKind,
  direction: receiptSettlementDirection,
  label: z.string(),
  amountPaise: moneyPaise,
  currencyCode: z.string(),
  foreignAmountMinor: moneyPaise.nullable(),
  fxRateToInrPaise: moneyPaise.nullable(),
  metadata: z.record(z.unknown()),
  createdAt: z.string().datetime(),
});

// ── record ────────────────────────────────────────────────────────
/**
 * Allocation invariant: Σ allocations.paise === amountPaise + tdsPaise.
 * The domain throws `AllocationMismatchError` if violated; the wire
 * schema only enforces shape (≥ 1 allocation, valid uuid+paise).
 */
export const recordInput = z.object({
  customerId: uuid,
  receivedOn: isoDate,
  amountPaise: moneyPaise,
  /** Defaults to '0' on the wire. The domain treats null/undefined/''
   *  identically. */
  tdsPaise: moneyPaise.optional(),
  /**
   * Realised FX rate (paise per 1.00 foreign minor unit) for receipts
   * allocated against foreign-currency invoices. Required when any
   * allocation references a non-INR invoice; rejected when all are INR.
   * ADR-0019.
   */
  settlementRatePaise: moneyPaise.optional(),
  mode: receiptMode,
  /** Bank reference, UPI transaction id, cheque number, etc. */
  reference: z.string().trim().max(64).optional(),
  /**
   * Account the money was received into (bank / cash). When set, the
   * receipt's amount credits this account's running balance. Optional for
   * back-compat with rows matched to a statement line later (V1.x).
   */
  bankAccountId: uuid.optional(),
  settlementLines: z.array(receiptSettlementLineInput).max(20).optional(),
  allocations: z
    .array(
      z.object({
        invoiceId: uuid,
        paise: moneyPaise,
      }),
    )
    .min(1),
});

export const recordOutput = z.object({
  id: uuid,
  /** Status flips triggered by this receipt — lets the SPA invalidate the
   *  exact rows that changed instead of the whole list. */
  statusTransitions: z.array(
    z.object({
      invoiceId: uuid,
      fromStatus: z.string(),
      toStatus: z.string(),
    }),
  ),
});

// ── get ───────────────────────────────────────────────────────────
export const getInput = z.object({ id: uuid });

export const receiptRow = z.object({
  id: uuid,
  customerId: uuid,
  receivedOn: isoDate,
  amountPaise: moneyPaise,
  tdsPaise: moneyPaise,
  fxGainLossPaise: signedPaise,
  /** Realised FX rate for foreign-invoice receipts. Null otherwise. ADR-0019. */
  settlementRatePaise: moneyPaise.nullable(),
  tdsSectionSnapshot: z.string().nullable(),
  mode: receiptMode,
  reference: z.string().nullable(),
  allocations: z.array(
    z.object({
      invoiceId: uuid,
      paise: moneyPaise,
      allocatedOn: isoDate,
    }),
  ),
  settlementLines: z.array(receiptSettlementLine),
  voidsId: uuid.nullable(),
  createdAt: z.string().datetime(),
});

export const getOutput = z.object({
  receipt: receiptRow,
  /** True when a reversing row voids this receipt (the original shows "Void"). */
  isVoided: z.boolean(),
  customer: z.object({
    id: uuid,
    name: z.string(),
    isTdsApplicable: z.boolean(),
    tdsSection: z.string().nullable(),
  }),
});

// ── void ──────────────────────────────────────────────────────────
/** Append-only reversal (ADR-0007). Founder-only; reverses the ledger posting
 *  and restores each allocated invoice's status. */
export const voidInput = z.object({
  id: uuid,
  reason: z.string().trim().min(1).max(280),
});
export const voidOutput = z.object({
  reversingId: uuid,
  statusTransitions: z.array(
    z.object({
      invoiceId: uuid,
      fromStatus: z.string(),
      toStatus: z.string(),
    }),
  ),
});

// ── list ──────────────────────────────────────────────────────────
export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  customerId: uuid.optional(),
  invoiceId: uuid.optional(),
});

const listRow = z.object({
  id: uuid,
  customerId: uuid,
  customerName: z.string(),
  receivedOn: isoDate,
  amountPaise: moneyPaise,
  tdsPaise: moneyPaise,
  fxGainLossPaise: signedPaise,
  /** Realised FX rate (paise per 1 foreign currency unit). Null for INR receipts. ADR-0019. */
  settlementRatePaise: moneyPaise.nullable(),
  /** Currency of the source invoice. INR for domestic receipts. ADR-0019. */
  currencyCode: z.string(),
  /** Foreign total of the source invoice. Null for INR receipts. */
  foreignAmountMinor: moneyPaise.nullable(),
  /** amount + tds (the gross billed-against-invoice amount). */
  appliedPaise: moneyPaise,
  mode: receiptMode,
  reference: z.string().nullable(),
  createdAt: z.string().datetime(),
});

export const listOutput = paged(listRow);

// ── derived TS types ──────────────────────────────────────────────
export type RecordInput = z.infer<typeof recordInput>;
export type RecordOutput = z.infer<typeof recordOutput>;
export type GetInput = z.infer<typeof getInput>;
export type GetOutput = z.infer<typeof getOutput>;
export type ListInput = z.infer<typeof listInput>;
export type ListOutput = z.infer<typeof listOutput>;
export type ReceiptRow = z.infer<typeof receiptRow>;
export type ListRow = z.infer<typeof listRow>;
export type VoidInput = z.infer<typeof voidInput>;
export type VoidOutput = z.infer<typeof voidOutput>;
export type ReceiptMode = z.infer<typeof receiptMode>;
export type ReceiptSettlementKind = z.infer<typeof receiptSettlementKind>;
export type ReceiptSettlementDirection = z.infer<typeof receiptSettlementDirection>;
